import { useCallback, useMemo } from 'react';

/**
 * Safely asks the browser to start playing the video element.
 *
 * The hook handles the promise returned by video.play(), ignores normal AbortError
 * failures, logs real play failures, and tells callers whether the play attempt worked.
 */
const useVideoPlayAttempt = ({ isCurrentRequest }) => {
  const playVideo = useCallback(
    async (video, requestId, reason = 'Video play attempt') => {
      if (!video || !isCurrentRequest(requestId)) {
        return { didPlay: false, wasAborted: false, wasStale: true, error: null };
      }

      try {
        const playAttempt = video.play();

        // If the play attempt is undefined or not a promise assume it worked (older browsers didn't return a promise from video.play())
        if (!playAttempt || typeof playAttempt.then !== 'function') {
          return { didPlay: true, wasAborted: false, wasStale: false, error: null };
        }

        await playAttempt;

        if (!isCurrentRequest(requestId)) {
          return { didPlay: false, wasAborted: false, wasStale: true, error: null };
        }

        return { didPlay: true, wasAborted: false, wasStale: false, error: null };
      } catch (error) {
        if (error?.name === 'AbortError') {
          return { didPlay: false, wasAborted: true, wasStale: false, error: null };
        }

        console.error(`${reason} failed:`, error);
        return { didPlay: false, wasAborted: false, wasStale: false, error: error };
      }
    },
    [isCurrentRequest],
  );

  return useMemo(
    () => ({
      playVideo,
    }),
    [playVideo],
  );
};

export default useVideoPlayAttempt;
